import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { getRoleName } from '../utils/rolePermissions';

const Header = ({ currentUser, currentPage, onToggleSidebar, onLogout, isMobile, sidebarCollapsed }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  
  // Usar el contexto de notificaciones
  const {
    notifications,
    unreadCount,
    markNotificationsAsRead,
    goToTask,
    getPriorityEmoji,
    formatDateOnly
  } = useNotifications();

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowMobileSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cerrar dropdowns al cambiar de página
  useEffect(() => {
    setShowNotifications(false);
    setShowProfile(false);
    setShowMobileSearch(false);
  }, [currentPage]);

  const getPageTitle = () => {
    switch (currentPage) {
      case 'dashboard':
        return 'Dashboard';
      case 'clients':
        return 'Gestión de Clientes';
      case 'allTasks':
        return 'Gestión de Tareas';
      case 'admin':
        return 'Panel de Administración';
      case 'budgetOperations':
        return 'Operaciones de Presupuesto';
      case 'campaignTracking':
        return 'Seguimiento de Campañas';
      case 'balanceReduction':
        return 'Clientes que Bajan Saldo';
      case 'noContactClients':
        return 'Clientes Sin Contacto';
      case 'noTrafficClients':
        return 'Clientes Sin Tráfico';
      case 'excessJunkClients':
        return 'Exceso de Basura';
      case 'cpcTrafficClients':
        return 'CPC y Tráfico';
      case 'conversionDrops':
        return 'Conversiones Caídas';
      case 'verifications':
        return 'Verificaciones';
      case 'noAnswerClients':
        return 'Clientes que No Contestan';
      default:
        return 'CRM System';
    }
  };

  const getPageDescription = () => {
    switch (currentPage) {
      case 'dashboard':
        return 'Resumen general y métricas del sistema';
      case 'clients':
        return 'Administra y visualiza información de clientes';
      case 'allTasks':
        return 'Gestiona tareas y seguimiento de actividades';
      case 'admin':
        return 'Configuración del sistema y usuarios';
      case 'budgetOperations':
        return 'Gestión de aumentos de presupuesto Google';
      case 'campaignTracking':
        return 'Seguimiento de campañas nuevas';
      case 'balanceReduction':
        return 'Clientes que reducen su saldo';
      case 'noContactClients':
        return 'Clientes que bajaron o sin contactos';
      case 'noTrafficClients':
        return 'Clientes sin tráfico web';
      case 'excessJunkClients':
        return 'Clientes con exceso de basura';
      case 'cpcTrafficClients':
        return 'Aumento CPC y disminución de tráfico';
      case 'conversionDrops':
        return 'Clientes con conversiones caídas';
      case 'verifications':
        return 'Verificaciones del sistema';
      case 'noAnswerClients':
        return 'Clientes que no contestan llamadas';
      default:
        return 'Sistema de gestión de relaciones con clientes';
    }
  };

  const getPageIcon = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
          </svg>
        );
      case 'clients':
        return (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'allTasks':
        return (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
      <div className="responsive-container">
        <div className="flex items-center justify-between responsive-padding-y">
          {/* Sección izquierda - Información de página */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
            {/* Botón de menú móvil */}
            <button
              onClick={onToggleSidebar}
              className="lg:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200 flex-shrink-0"
              aria-label="Abrir menú"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Botón de colapsar sidebar - Desktop */}
            <button
              onClick={onToggleSidebar}
              className="hidden lg:flex p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200 flex-shrink-0"
              aria-label={sidebarCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
              title={sidebarCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
            >
              {sidebarCollapsed ? (
                // Icono para expandir (sidebar colapsado)
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              ) : (
                // Icono para colapsar (sidebar expandido)
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
                </svg>
              )}
            </button>
            
            {/* Icono y título de página */}
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white shadow-lg">
                {getPageIcon()}
              </div>
              
              <div className="min-w-0 flex-1">
                <h1 className="responsive-text-lg font-bold text-gray-900 truncate">
                  {getPageTitle()}
                </h1>
                <p className="responsive-text-xs text-gray-500 truncate desktop-only">
                  {getPageDescription()}
                </p>
              </div>
            </div>
          </div>

          {/* Sección derecha - Acciones */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            {/* Barra de búsqueda - Desktop */}
            <div className="hidden lg:flex items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 xl:w-80 pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Botón de búsqueda móvil */}
            <div className="lg:hidden relative" ref={searchRef}>
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="p-2.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-xl transition-all duration-200"
                aria-label="Buscar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Búsqueda móvil expandida */}
              {showMobileSearch && (
                <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notificaciones - Responsivas */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) {
                    markNotificationsAsRead();
                  }
                }}
                className="p-2.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-xl transition-all duration-200 relative"
                aria-label="Notificaciones"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM11 17H6l5 5v-5zM12 3v12" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-lg notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Panel de notificaciones responsivo */}
              {showNotifications && (
                <div className={`absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden ${
                  isMobile ? 'w-80 max-w-[90vw]' : 'w-96'
                }`}>
                  <div className="responsive-padding border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="responsive-text-base font-semibold text-gray-900">
                        🔔 Notificaciones
                      </h3>
                      <span className="responsive-text-xs text-gray-500">
                        {notifications.length} notificación{notifications.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="responsive-padding text-center">
                        <div className="text-4xl mb-2">🔔</div>
                        <p className="text-gray-500 responsive-text-sm">No hay notificaciones nuevas</p>
                      </div>
                    ) : (
                      <div className="p-2 sm:p-3">
                        {notifications.map((notification, index) => (
                          <div 
                            key={notification.id || index}
                            className={`p-3 sm:p-4 rounded-xl mb-2 sm:mb-3 transition-all duration-200 ${
                              notification.isRead 
                                ? 'bg-gray-50 border border-gray-200' 
                                : 'bg-blue-50 border border-blue-200 shadow-sm'
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {notification.type === 'new_task' ? (
                                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                                    📋
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                    💬
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="responsive-text-sm font-medium text-gray-900 truncate">
                                    {notification.title}
                                  </h4>
                                  <span className="responsive-text-xs text-gray-500 flex-shrink-0 ml-2">
                                    {formatDateOnly(notification.createdAt)} {new Date(notification.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                
                                <p className="responsive-text-sm text-gray-700 mb-2">
                                  {notification.message}
                                </p>
                                
                                {notification.clientName && (
                                  <p className="responsive-text-xs text-gray-500 mb-2">
                                    Cliente: {notification.clientName}
                                  </p>
                                )}
                                
                                {notification.priority && (
                                  <div className="flex items-center space-x-2 mb-2">
                                    <span className="responsive-text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                                      {getPriorityEmoji(notification.priority)} {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                                    </span>
                                  </div>
                                )}
                                
                                {notification.commentText && (
                                  <div className="mt-2 p-2 bg-white border border-gray-200 rounded-lg responsive-text-xs">
                                    <span className="font-medium">Comentario:</span> "{notification.commentText}"
                                  </div>
                                )}
                                
                                {notification.taskId && (
                                  <div className="mt-3">
                                    <button
                                      onClick={() => {
                                        goToTask(notification.taskId);
                                        setShowNotifications(false);
                                      }}
                                      className="responsive-text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                    >
                                      Ver Tarea
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {notifications.length > 0 && (
                    <div className="responsive-padding border-t border-gray-200 bg-gray-50">
                      <button
                        onClick={() => {
                          markNotificationsAsRead();
                          setShowNotifications(false);
                        }}
                        className="w-full responsive-text-sm px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                      >
                        Marcar todas como leídas
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Perfil de usuario - Responsivo */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-xl hover:bg-gray-100 transition-all duration-200"
                aria-label="Perfil de usuario"
              >
                <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg ${
                  isMobile ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'
                }`}>
                  {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                </div>
                <div className="hidden md:block text-left min-w-0">
                  <p className="responsive-text-sm font-medium text-gray-900 truncate">
                    {currentUser.firstName} {currentUser.lastName}
                  </p>
                  <p className="responsive-text-xs text-gray-500 capitalize truncate">
                    {getRoleName(currentUser.role)}
                  </p>
                </div>
                <svg className="w-4 h-4 text-gray-400 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown del perfil responsivo */}
              {showProfile && (
                <div className={`absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 ${
                  isMobile ? 'w-64' : 'w-72'
                }`}>
                  <div className="responsive-padding border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                        {currentUser.firstName?.charAt(0)}{currentUser.lastName?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="responsive-text-sm font-semibold text-gray-900 truncate">
                          {currentUser.firstName} {currentUser.lastName}
                        </p>
                        <p className="responsive-text-xs text-gray-500 truncate">{currentUser.email}</p>
                        <p className="responsive-text-xs text-gray-400 capitalize truncate">
                          {getRoleName(currentUser.role)}
                        </p>
                        {currentUser.role === 'standard' && currentUser.cells && currentUser.cells.length > 0 && (
                          <p className="responsive-text-xs text-gray-400 truncate mt-1">
                            📍 {currentUser.cells.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-2">
                    <button 
                      onClick={() => {
                        setShowProfile(false);
                        alert('Funcionalidad de perfil en desarrollo');
                      }}
                      className="w-full text-left px-3 py-2.5 responsive-text-sm text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Mi Perfil</span>
                    </button>
                    <button 
                      onClick={() => {
                        setShowProfile(false);
                        alert('Funcionalidad de configuración en desarrollo');
                      }}
                      className="w-full text-left px-3 py-2.5 responsive-text-sm text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Configuración</span>
                    </button>
                    <div className="border-t border-gray-200 my-2"></div>
                    <button 
                      onClick={() => {
                        setShowProfile(false);
                        onLogout();
                      }}
                      className="w-full text-left px-3 py-2.5 responsive-text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors flex items-center space-x-2 font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
